import { useEffect, useReducer, useRef } from 'react'
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr'
import { hubUrl, jobsApi } from '../api/jobs'
import { session } from '../api/client'
import type { JobEvent, LiveJob, VoiceShape } from '../types/jobs'
import type { Job, JobLogLine, JobView } from '../types/voice'

// One job, live. Loads the snapshot, opens the owner's hub stream and folds every event into the
// snapshot as it arrives; if the socket is down it polls instead, so the page is never stale by
// more than a couple of seconds. The hub sends only this owner's jobs (the server groups by
// principal), so the hook filters by id and nothing else.

type Action =
  | { type: 'snapshot'; view: JobView }
  | { type: 'event'; event: JobEvent }
  | { type: 'channel'; channel: LiveJob['channel'] }

const terminal = (s: Job['status']) => s === 'Completed' || s === 'Failed' || s === 'Cancelled'

function parseShape(json: string | null): VoiceShape | null {
  if (!json) return null
  try { return JSON.parse(json) as VoiceShape } catch { return null }
}

function reduce(state: LiveJob | null, action: Action): LiveJob | null {
  switch (action.type) {
    case 'snapshot': {
      const shape = parseShape(action.view.job.shapeJson)
      // a later live event never loses to an older snapshot
      if (state && state.lastEventAt && state.lastEventAt > action.view.job.createdAt && !terminal(action.view.job.status)) return { ...state, log: mergeLog(state.log, action.view.log) }
      return { job: action.view.job, log: action.view.log, shape: shape ?? state?.shape ?? null, channel: state?.channel ?? 'connecting', lastEventAt: state?.lastEventAt ?? null }
    }
    case 'channel':
      return state ? { ...state, channel: action.channel } : state
    case 'event': {
      if (!state || action.event.jobId !== state.job.id) return state
      const e = action.event
      const steps = state.job.steps.map((s) => {
        if (s.stepId !== e.stepId) return s
        const next = { ...s }
        if (e.stepStatus) next.status = e.stepStatus
        if (e.level === 'step' && e.stepStatus === 'Running') { next.startedAt = e.at; next.completedAt = null; next.done = 0; next.total = null; next.rate = null; next.etaSeconds = null }
        if (e.level === 'step' && e.stepStatus !== 'Running') { next.completedAt = e.at; next.message = e.message; next.done = e.done; next.total = e.total ?? next.total; next.etaSeconds = 0 }
        if (e.level === 'progress') { next.done = e.done; next.total = e.total; next.rate = e.rate; next.etaSeconds = e.etaSeconds }
        if (e.warnings > next.warnings) next.warnings = e.warnings
        return next
      })
      const job: Job = {
        ...state.job,
        status: e.status,
        steps,
        startedAt: state.job.startedAt ?? (e.status === 'Running' ? e.at : null),
        completedAt: terminal(e.status) ? e.at : state.job.completedAt,
        error: e.level === 'error' ? e.message : state.job.error,
        warnings: steps.reduce((n, s) => n + s.warnings, 0),
        // the kind's latest picture rides on the event too; the completion line reads it before the snapshot lands
        shapeJson: e.shapeJson ?? state.job.shapeJson,
      }
      const log: JobLogLine[] = e.level === 'info' || e.level === 'warn' || e.level === 'error'
        ? (e.message && e.stepId ? [...state.log, { at: e.at, stepId: e.stepId, level: e.level, message: e.message }] : state.log)
        : state.log
      return { job, log, shape: parseShape(e.shapeJson) ?? state.shape, channel: state.channel, lastEventAt: e.at }
    }
  }
}

function mergeLog(mine: JobLogLine[], theirs: JobLogLine[]): JobLogLine[] {
  return theirs.length >= mine.length ? theirs : mine
}

export function useJobLive(spoke: string, jobId: string | undefined): { state: LiveJob | null; error: Error | null } {
  const [state, dispatch] = useReducer(reduce, null)
  const errorRef = useRef<Error | null>(null)
  const [, bump] = useReducer((n: number) => n + 1, 0)

  useEffect(() => {
    if (!jobId) return
    let disposed = false
    let poll: number | undefined

    const load = async () => {
      try {
        const view = await jobsApi.get(spoke, jobId)
        if (!disposed) dispatch({ type: 'snapshot', view })
        return view
      } catch (err) {
        errorRef.current = err instanceof Error ? err : new Error(String(err))
        bump()
        return null
      }
    }

    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl, { accessTokenFactory: () => session.get() ?? '' })
      .withAutomaticReconnect([0, 1000, 3000, 8000, 15000])
      .configureLogging(LogLevel.Warning)
      .build()

    connection.on('job', (event: JobEvent) => { if (!disposed) dispatch({ type: 'event', event }) })
    connection.onreconnecting(() => dispatch({ type: 'channel', channel: 'polling' }))
    connection.onreconnected(() => { dispatch({ type: 'channel', channel: 'live' }); void load() })
    connection.onclose(() => dispatch({ type: 'channel', channel: 'polling' }))

    const startPolling = () => {
      if (poll !== undefined) return
      poll = window.setInterval(async () => {
        const view = await load()
        if (view && terminal(view.job.status)) { window.clearInterval(poll); poll = undefined }
      }, 2000)
    }

    void (async () => {
      const first = await load()
      if (disposed || (first && terminal(first.job.status))) return
      try {
        await connection.start()
        if (disposed) { await connection.stop(); return }
        dispatch({ type: 'channel', channel: 'live' })
        await load() // anything that happened between the snapshot and the socket opening
        // a safety net under the live stream: a slow poll catches a missed landing
        poll = window.setInterval(async () => {
          if (connection.state !== HubConnectionState.Connected) return
          const view = await load()
          if (view && terminal(view.job.status)) { window.clearInterval(poll); poll = undefined }
        }, 10000)
      } catch {
        dispatch({ type: 'channel', channel: 'polling' })
        startPolling()
      }
    })()

    return () => {
      disposed = true
      if (poll !== undefined) window.clearInterval(poll)
      void connection.stop()
    }
  }, [spoke, jobId])

  return { state, error: errorRef.current }
}
